import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { KeyRound, LogIn, Plug } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorState } from "@/components/ErrorState";
import { KeyOnceModal } from "@/components/KeyOnceModal";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { PauseControl } from "@/components/PauseControl";
import { SignInMethods } from "@/components/SignInMethods";
import { toUserMessage } from "@/lib/errors";
import {
  useGatewayKeys,
  useProviderKeys,
  useRevokeGatewayKey,
  useRotateGatewayKey,
  useSaveProviderKey,
  type GatewayKeyInfo,
} from "@/api/hooks/use-settings-keys";
import { PROVIDERS } from "@/lib/providers";

const TABS = ["provider-keys", "gateway-keys", "sign-in"] as const;
type Tab = (typeof TABS)[number];
// Keys are an admin's business; every Member manages their own sign-in.
const MEMBER_TABS: readonly Tab[] = ["sign-in"];

export function Settings() {
  // Deep-linkable from the onboarding guide (e.g. /settings?tab=gateway-keys).
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive the active tab straight from the URL so deep-links from the
  // onboarding guide (e.g. /settings?tab=gateway-keys) take effect even when
  // Settings is already mounted — a query-only change re-renders but does not
  // remount, so seeding useState once would ignore the new tab.
  const { user } = useAuth();
  const allowed: readonly Tab[] = user?.role === "admin" ? TABS : MEMBER_TABS;
  // Connecting GitHub or Google comes back to /settings?tab=sign-in.
  const requested = searchParams.get("tab");
  const tab: Tab = allowed.find((t) => t === requested) ?? allowed[0] ?? "sign-in";

  const selectTab = (id: Tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", id);
      // One-time notices from connecting GitHub or Google.
      for (const key of ["connected", "error", "via"]) next.delete(key);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold text-fg">Settings</h1>

      <PauseControl />

      <div className="flex gap-1 border-b border-line-subtle">
        {(
          [
            { id: "provider-keys", label: "Provider Keys", icon: Plug },
            { id: "gateway-keys", label: "Gateway Keys", icon: KeyRound },
            { id: "sign-in", label: "Sign-in", icon: LogIn },
          ] as const
        )
          .filter(({ id }) => allowed.includes(id))
          .map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => selectTab(id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm transition-colors ${
              tab === id
                ? "border-electric-blue font-medium text-electric-blue"
                : "border-transparent text-fg-muted hover:text-fg"
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {tab === "provider-keys" && <ProviderKeysTab />}
      {tab === "gateway-keys" && <GatewayKeysTab />}
      {tab === "sign-in" && <SignInMethods />}
    </div>
  );
}

// ── Provider Keys ─────────────────────────────────────────────────────────

function ProviderKeysTab() {
  const { data, isLoading, isError, refetch } = useProviderKeys();

  if (isLoading) return <LoadingSkeleton rows={3} />;
  if (isError)
    return <ErrorState message="Couldn't load provider keys." onRetry={() => refetch()} />;

  const byProvider = new Map(data?.providers.map((p) => [p.provider, p]));

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-fg-muted">
        Your API keys are validated against the provider when you save them,
        stored encrypted, and never shown again — only the last four characters
        stay visible. We recommend creating a dedicated key for Phantm in your
        provider's console.
      </p>
      {PROVIDERS.map((p) => (
        <ProviderKeyCard
          key={p.id}
          provider={p.id}
          label={p.label}
          placeholder={p.placeholder}
          existing={byProvider.get(p.id) ?? null}
        />
      ))}
    </div>
  );
}

function ProviderKeyCard({
  provider,
  label,
  placeholder,
  existing,
}: {
  provider: string;
  label: string;
  placeholder: string;
  existing: { last4: string | null; created_at: string | null } | null;
}) {
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const save = useSaveProviderKey();

  async function doSave() {
    setConfirming(false);
    setError("");
    setSaved(false);
    try {
      await save.mutateAsync({ provider, apiKey: draft.trim() });
      setDraft("");
      setSaved(true);
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    if (existing) {
      setConfirming(true); // replacing a live key — confirm first
    } else {
      void doSave();
    }
  }

  return (
    <div className="rounded-lg border border-line-subtle bg-surface p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">{label}</h2>
        {existing ? (
          <span className="font-mono text-xs text-fg-muted">
            ••••{existing.last4 ?? "····"}
            {existing.created_at && (
              <span className="ml-2 text-fg-faint">
                added {new Date(existing.created_at).toLocaleDateString()}
              </span>
            )}
          </span>
        ) : (
          <span className="text-xs text-fg-faint">No key on file</span>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {error}
        </div>
      )}
      {saved && (
        <div className="rounded-md border border-safe-green/30 bg-safe-green/10 px-3 py-2 text-sm text-safe-green">
          Key validated and saved.
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={existing ? `Replace key (${placeholder})` : placeholder}
          autoComplete="off"
          spellCheck={false}
          className="flex-1 rounded-md border border-line bg-canvas px-3 py-2 font-mono text-sm text-fg placeholder:font-sans placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
        />
        <button
          type="submit"
          disabled={!draft.trim() || save.isPending}
          className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending ? "Validating…" : existing ? "Replace" : "Save"}
        </button>
      </form>

      {confirming && (
        <ConfirmDialog
          title={`Replace ${label} key?`}
          message="Requests will switch to the new key immediately. The current key stays usable at the provider — revoke it there once you've confirmed everything works."
          confirmLabel="Replace key"
          variant="primary"
          isPending={save.isPending}
          onConfirm={() => void doSave()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

// ── Gateway Keys ──────────────────────────────────────────────────────────

function GatewayKeysTab() {
  const { data, isLoading, isError, refetch } = useGatewayKeys();
  const rotate = useRotateGatewayKey();
  const revoke = useRevokeGatewayKey();

  // Held only here; dropped when the modal is dismissed.
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [revokeExisting, setRevokeExisting] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<GatewayKeyInfo | null>(null);
  const [error, setError] = useState("");

  if (isLoading) return <LoadingSkeleton rows={3} />;
  if (isError)
    return <ErrorState message="Couldn't load gateway keys." onRetry={() => refetch()} />;

  const keys = data?.keys ?? [];
  const activeCount = keys.filter((k) => !k.revoked_at).length;

  async function doRotate() {
    setConfirmRotate(false);
    setError("");
    try {
      const result = await rotate.mutateAsync({ revokeExisting });
      setFreshKey(result.gateway_key);
      setRevokeExisting(false);
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  async function doRevoke(target: GatewayKeyInfo) {
    setRevokeTarget(null);
    setError("");
    try {
      const isLast = activeCount <= 1;
      await revoke.mutateAsync({ keyId: target.id, confirmLockout: isLast });
    } catch (err) {
      setError(toUserMessage(err));
    }
  }

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-fg-muted">
        Gateway keys authenticate your requests to Phantm. Rotating creates a
        new key without disabling existing ones, so you can switch over with
        zero downtime — revoke old keys once you've migrated.
      </p>

      {error && (
        <div className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-line-subtle bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-subtle text-left text-xs uppercase tracking-wider text-fg-faint">
              <th className="px-4 py-3 font-medium">Key</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-line-subtle last:border-0">
                <td className="px-4 py-3 font-mono text-xs text-fg">{k.prefix}</td>
                <td className="px-4 py-3 text-fg-muted">
                  {k.created_at ? new Date(k.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  {k.revoked_at ? (
                    <span className="text-xs text-fg-faint">Revoked</span>
                  ) : (
                    <span className="text-xs text-safe-green">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {!k.revoked_at && (
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(k)}
                      className="text-xs text-fg-muted transition-colors hover:text-block-red"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setConfirmRotate(true)}
          disabled={rotate.isPending}
          className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:opacity-40"
        >
          {rotate.isPending ? "Rotating…" : "Rotate key"}
        </button>
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            checked={revokeExisting}
            onChange={(e) => setRevokeExisting(e.target.checked)}
          />
          Also revoke existing keys
        </label>
      </div>

      {confirmRotate && (
        <ConfirmDialog
          title="Rotate gateway key?"
          message={
            revokeExisting
              ? "A new key will be created and ALL existing keys revoked immediately. Any traffic still using them will fail until you switch to the new key."
              : "A new key will be created. Existing keys keep working until you revoke them, so you can migrate with zero downtime."
          }
          confirmLabel="Create new key"
          variant={revokeExisting ? "danger" : "primary"}
          isPending={rotate.isPending}
          onConfirm={() => void doRotate()}
          onCancel={() => setConfirmRotate(false)}
        />
      )}

      {revokeTarget && (
        <ConfirmDialog
          title={activeCount <= 1 ? "Revoke your LAST active key?" : "Revoke this key?"}
          message={
            activeCount <= 1
              ? "This is your only active key — revoking it stops ALL gateway traffic for your account until you rotate a new one."
              : `Requests using ${revokeTarget.prefix} will start failing immediately.`
          }
          confirmLabel="Revoke key"
          isPending={revoke.isPending}
          onConfirm={() => void doRevoke(revokeTarget)}
          onCancel={() => setRevokeTarget(null)}
        />
      )}

      {freshKey && <KeyOnceModal gatewayKey={freshKey} onDismiss={() => setFreshKey(null)} />}
    </div>
  );
}
